import { ButtonSecondary, Text } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';

import type { EditableMeetingTemplate } from '@/types/thematic-meetings';

import { userInstanceAtom } from '@/atoms/userDetails';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { NewTemplateCard } from '@/components/TemplateCard/NewTemplateCard';
import { TemplateCardSkeleton } from '@/components/TemplateCard/TemplateCardSkeleton';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CollapsibleSectionHeading } from '@/pages/ManageTemplates/components/CollapsibleSectionHeading';
import { MeetingPlanTemplateCard } from '@/pages/ManageTemplates/components/MeetingPlanTemplateSection/MeetingPlanTemplateCard';
import { NonCollapsingContent } from '@/pages/ManageTemplates/components/NonCollapsingContent';
import { getTextByLanguage } from '@/utils/localization';

const SKELETON_COUNT = 5;
const CARD_GRID = 'grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3';

export type MeetingPlanTemplateSectionProps = {
    templates: EditableMeetingTemplate[];
    isLoading: boolean;
    isError: boolean;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    onSelectTemplate: (templateId: string) => void;
    onCreateTemplate: () => void;
    onImportTemplate: () => void;
};

/**
 * The plans the company's thematic meetings are built from, listed beside its evaluation templates.
 * A tile opens the plan it shows, for the company to reword. Like the evaluation sections, the first
 * tile writes a new plan and the heading imports one from the Amelio catalogue.
 */
export const MeetingPlanTemplateSection = ({
    templates,
    isLoading,
    isError,
    isCollapsed,
    onToggleCollapse,
    onSelectTemplate,
    onCreateTemplate,
    onImportTemplate,
}: MeetingPlanTemplateSectionProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const loggedUser = useAtomValue(userInstanceAtom);

    // The new-plan tile needs nothing from the list, so it stays first whatever the list's state;
    // a loading list never reads as an empty one, and a failed one says so beneath the tile.
    const planTiles = () => {
        if (isLoading) {
            return Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <TemplateCardSkeleton key={index} />
            ));
        }

        return templates.map((template) => (
            <MeetingPlanTemplateCard
                key={template.id}
                title={getTextByLanguage(template.title, language)}
                objective={getTextByLanguage(template.objective, language)}
                onClick={() => onSelectTemplate(template.id)}
                isDraft={!template.isActive}
            />
        ));
    };

    const listNotice = () => {
        if (isLoading) {
            return null;
        }

        if (isError) {
            return <EmptyState message={t('templateManagement.template.meetingPlans.error')} />;
        }

        if (templates.length === 0) {
            return <EmptyState message={t('templateManagement.template.meetingPlans.empty')} />;
        }

        return null;
    };

    return (
        <>
            <CollapsibleSectionHeading
                heading={t('templateManagement.template.meetingPlans.heading')}
                isCollapsed={isCollapsed}
                onToggleCollapse={onToggleCollapse}
            >
                {!loggedUser?.isAmelioAdmin && (
                    <ButtonSecondary
                        size={'md'}
                        className={'mt-4 border-none sm:mt-0'}
                        onClick={(event) => {
                            event.stopPropagation();
                            onImportTemplate();
                        }}
                    >
                        <Text
                            weight={'medium'}
                            size={'small'}
                        >
                            {t('templateManagement.template.importTemplateText')}
                        </Text>
                    </ButtonSecondary>
                )}
            </CollapsibleSectionHeading>

            {!isCollapsed && (
                <NonCollapsingContent>
                    <div
                        className={CARD_GRID}
                        aria-busy={isLoading}
                        data-testid={isLoading ? 'meeting-plan-templates-loading' : undefined}
                    >
                        <NewTemplateCard
                            title={t('templateManagement.template.meetingPlans.newPlan.title')}
                            description={t(
                                'templateManagement.template.meetingPlans.newPlan.description',
                            )}
                            iconName={'essential-add'}
                            onButtonClick={onCreateTemplate}
                        />

                        {planTiles()}
                    </div>

                    {listNotice()}
                </NonCollapsingContent>
            )}
        </>
    );
};
